import { Clock, Presentation } from 'lucide-react'
import { registerDocHostClient } from '../../lib/doc-hosts'
import { trpcClient } from '../../lib/trpc-client'
import type { DocHostClient } from '../../ui/formats/DocView'

function pdfBlob(base64: string): Blob {
  const bytes = Uint8Array.from(atob(base64), (character) =>
    character.charCodeAt(0),
  )
  return new Blob([bytes], { type: 'application/pdf' })
}

const studioDeckClient: DocHostClient = {
  view: (ref) => ({
    queryKey: ['studio-deck', ref.spaceId, ref.key, 'doc-view'],
    queryFn: async () => {
      const deck = await trpcClient.deck.view.query({
        spaceId: ref.spaceId,
        key: ref.key,
      })
      return {
        format: 'slides' as const,
        title: deck.title,
        facts: [
          {
            label: 'Slides',
            icon: Presentation,
            value: deck.slides.length.toLocaleString(),
          },
          {
            label: 'Last edited',
            icon: Clock,
            value: new Date(deck.updatedAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }),
          },
        ],
        downloads: deck.downloads,
        extra: {
          slides: deck.slides,
          canWrite: deck.access.owner || deck.access.can.includes('write'),
        },
      }
    },
  }),
  previewTicket: async (ref, slideId) => {
    if (!slideId) throw new Error('Choose a slide to preview.')
    const [ticket] = await trpcClient.deck.previewTickets.mutate({
      spaceId: ref.spaceId,
      key: ref.key,
      slideIds: [slideId],
    })
    if (!ticket) throw new Error('The slide preview could not be prepared.')
    return { url: ticket.url }
  },
  download: async (ref, variant) => {
    if (variant !== 'slides' && variant !== 'notes')
      throw new Error('Choose which deck PDF to download.')
    const result = await trpcClient.deck.download.mutate({
      spaceId: ref.spaceId,
      key: ref.key,
      mode: variant,
    })
    return { blob: pdfBlob(result.base64), filename: result.filename }
  },
  updateSlideNotes: (ref, slide) =>
    trpcClient.deck.updateSlide.mutate({
      spaceId: ref.spaceId,
      key: ref.key,
      slideId: slide.id,
      version: slide.version,
      notes: slide.notes,
    }),
  moveSlide: (ref, slide) =>
    trpcClient.deck.moveSlide.mutate({
      spaceId: ref.spaceId,
      key: ref.key,
      slideId: slide.id,
      version: slide.version,
      after: slide.after,
      before: slide.before,
    }),
}

registerDocHostClient('studio-deck', studioDeckClient)
